import { Component, Inject, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, Output, EventEmitter, HostListener } from '@angular/core';
import { IWindowService } from 'angular';
import { Subscription } from 'rxjs';
import { BaseGroupType } from 'src/AngularJS/Models/group-type.model';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { GroupSelectorService } from '../group-selector/group-selector.service';
import { CvConfigurationService } from '../services/cv-configuration.service';
import { CVLocalizationService } from '../services/cv-localization.service';
import { ClearViewUtils } from 'src/shared/utils';
import { DashboardManager } from 'src/shared/models/dashboard-manager';

export type GroupViewBySelectorCustom = {
    id: string;
    type: BaseGroupType;
    value: number;
    display: string;
    index?: number;
    filterableGroupingDTO?: FilterableGroupingDTO;
};

export type FilterableGroupingDTO = {
    ValueField: string;
    ValueDisplayField: string;
    ValueSourceReportingSet: string;
    FilterableDisplayName: string;
}

@Component({
    selector: 'cv-group-view-by-selector',
    templateUrl: './group-view-by-selector.component.html',
    styleUrls: ['./group-view-by-selector.component.scss']
})
export class GroupViewBySelectorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() stateKey: string;
    @Input() settingsManager: SlideoutSettingsManager;
    @Input() includeCustom: GroupViewBySelectorCustom[] = [];
    @Input() removePocFilter = false;
    @Input() settingViewByLimitsFilters = 0;
    @Input() dashboardManager: DashboardManager

    @Output() selectorClicked = new EventEmitter<void>();

    @HostListener('click', ['$event'])
    @HostListener('keydown', ['$event'])
    handleInteraction(event: MouseEvent | KeyboardEvent) {
        if (this.dashboardManager?.isPrebuiltReport && this.config.isCxOne) {
            event.preventDefault();
            event.stopPropagation();
            this.selectorClicked.emit();
        }
    }

    options: GroupViewBySelectorCustom[] = [];
    viewByModel: GroupViewBySelectorCustom = null;

    private subscriptions: Subscription[] = [];
    private itemsByType = new Map<BaseGroupType, Map<number | string, GroupViewBySelectorCustom>>();

    constructor(
        @Inject('$window') private $window: IWindowService,
        public config: CvConfigurationService,
        private groupSelectorService: GroupSelectorService,
        private cvLocalizationService: CVLocalizationService,
    ) {

    }

    async ngOnInit() {
        await this.cvLocalizationService.GetSection('Group_View_By_Selector');

        this.updateModelFromManager();
        this.subscriptions.push(this.settingsManager.managerViewByUpdated.subscribe(_ => this.updateModelFromManager()));
        this.subscriptions.push(this.settingsManager.empGroupTypesChanged.subscribe(_ => this.updateOptions()));
        this.subscriptions.push(this.settingsManager.recGroupTypesChanged.subscribe(_ => this.updateOptions()));
        this.subscriptions.push(this.settingsManager.onSelectedGroupViewByChanged.subscribe((s) => {
            this.reloadMetrics();
        }));
    }

    reloadMetrics() {
        if (this.$window.MetricSelectorRefresh) {
            this.$window.MetricSelectorRefresh(`${this.stateKey}`);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s?.unsubscribe());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.includeCustom) {
            this.updateOptions();
        }
    }

    async onModelChanged() {
        if (this.viewByModel != null) {
            if(this.viewByModel.filterableGroupingDTO){
                this.settingsManager.onSelectedLevelChanged({
                    groupType: this.viewByModel.type,
                    groupLevel: this.viewByModel.value,
                    groupLevelName: this.viewByModel.display,
                    filterableGroupingDTO: this.viewByModel.filterableGroupingDTO,
                });
            }
            else{
                this.settingsManager.onSelectedLevelChanged({
                    groupType: this.viewByModel.type,
                    groupLevel: this.viewByModel.value,
                    groupLevelName: this.viewByModel.display,
                });
            }

            if (this.viewByModel.filterableGroupingDTO) {
                //I have to add the false, which is the same as the default, so it uses the optional one
                await this.groupSelectorService.saveSelectedToggle(
                    this.stateKey, this.viewByModel.type, this.viewByModel.value, false, this.viewByModel.filterableGroupingDTO);
            }
            else{
                await this.groupSelectorService.saveSelectedToggle(this.stateKey, this.viewByModel.type, this.viewByModel.value);
            }

            // if the selector has a selection limit, the group view change event will set the correct groups
            if(this.settingViewByLimitsFilters === 0 || this.viewByModel.type === BaseGroupType.Employee)
                this.settingsManager.onSelectedEmpGroupsChanged.next(this.settingsManager.empSelectorDTO);
            if(this.settingViewByLimitsFilters === 0 || this.viewByModel.type === BaseGroupType.Recording)
                this.settingsManager.onSelectedRecGroupsChanged.next(this.settingsManager.recSelectorDTO);
            this.settingsManager?.onSelectedGroupViewByChanged.next({
                GroupType: this.viewByModel.type,
                GroupLevel: this.viewByModel.value,
            });
        }
    }

    updateModelFromManager() {
        const { selectedToggle, selectedLevel, filterableGroupingDTO } = this.settingsManager;
        const itemsForType = this.itemsByType.get(selectedToggle);
        const key = filterableGroupingDTO ? filterableGroupingDTO.ValueField : selectedLevel;
        const model = itemsForType?.get(key);

        if (model && this.viewByModel !== model) {
            this.viewByModel = model;
        }
    }

    updateOptions() {
        this.itemsByType = new Map();
        this.options = [];
        let index = 0;
        if (this.settingsManager.empGroupTypes) {
            this.itemsByType.set(BaseGroupType.Employee, new Map());
            for (const empGroup of this.settingsManager.empGroupTypes) {
                const model = {
                    id: '',
                    type: BaseGroupType.Employee,
                    value: empGroup.level,
                    index: index++,
                    display: this.cvLocalizationService.getCachedText('Group_View_By_Selector', ClearViewUtils.standardizeLocalizationKey(empGroup.name), empGroup.name)
                };
                model.id = `${model.type}_${model.value}`;
                this.itemsByType.get(BaseGroupType.Employee).set(empGroup.level, model);
                this.options.push(model);
            }
        }

        if (this.settingsManager.recGroupTypes) {
            this.itemsByType.set(BaseGroupType.Recording, new Map());
            for (const recGroup of this.settingsManager.recGroupTypes) {
                if (this.removePocFilter && recGroup.level === 1) {
                    continue;
                }

                const model = {
                    id: '',
                    type: BaseGroupType.Recording,
                    value: recGroup.level,
                    index: index++,
                    display: this.cvLocalizationService.getCachedText('Group_View_By_Selector', ClearViewUtils.standardizeLocalizationKey(recGroup.name), recGroup.name)
                };
                model.id = `${model.type}_${model.value}`;
                this.itemsByType.get(BaseGroupType.Recording).set(recGroup.level, model);
                this.options.push(model);
            }
        }

        if (this.includeCustom) {
            for (const custom of this.includeCustom) {
                if (!this.itemsByType.has(custom.type))
                    this.itemsByType.set(custom.type, new Map());
                if (custom.type === BaseGroupType.Filterables){
                    this.itemsByType.get(custom.type).set(custom.filterableGroupingDTO.ValueField, custom);
                }
                else {
                    this.itemsByType.get(custom.type).set(custom.value, custom);
                }
                if (custom.display) {
                    custom.display = this.cvLocalizationService.getCachedText('Group_View_By_Selector', ClearViewUtils.standardizeLocalizationKey(custom.display), custom.display);
                }
                custom.index = index++;
                this.options.push(custom);
            }
        }

        this.updateModelFromManager();
    }
}
